/** @file Keeps the selection on live entities: a document change drops the ids that left it. */
import { currentDoc } from './currentDoc';
import { docStore } from './docStore';
import { pruneSelection } from './pruneSelection';
import { selectionStore } from './selectionStore';

const shared: { users: number; stop: (() => void) | null } = { users: 0, stop: null };

/** Prunes the selection against the viewed document. */
function prune(): void {
  const doc = currentDoc(docStore.getState());
  if (doc === null || shared.stop === null) return;
  const next = pruneSelection(selectionStore.getState(), doc);
  if (next !== null) selectionStore.setState(next);
}

/** One subscription; the prune runs in a microtask after the change, so the id remap of a commit answer (written right after
 *  the document) reaches the selection first, and several changes in one tick prune once. */
function subscribe(): () => void {
  let queued = false;
  const stop = docStore.subscribe(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; prune(); });
  });
  return stop;
}

/** Starts pruning (shared by every document panel that mounts; the last disposer stops it). Writes the selection only
 *  when an id was dropped, because every selection write clears the Inspector focus. */
export function installSelectionPrune(): () => void {
  if (shared.users === 0) shared.stop = subscribe();
  shared.users += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    shared.users -= 1;
    if (shared.users === 0) { shared.stop?.(); shared.stop = null; }
  };
}
